import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from "react-router";
import { toast } from 'sonner';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOrganizer: boolean;
  isFeaturedOrganizer: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ user: User | null }>;
  signOut: () => Promise<void>;
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [isFeaturedOrganizer, setIsFeaturedOrganizer] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const checkAdminStatus = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Error checking admin status:', error);
      }
      return { isAdmin: false, isSuperAdmin: false, isOrganizer: false, isFeaturedOrganizer: false };
    }

    const roles = data?.map(r => r.role) || [];
    return {
      isAdmin: roles.includes('admin') || roles.includes('superadmin'),
      isSuperAdmin: roles.includes('superadmin'),
      isOrganizer: roles.includes('organizer'),
      isFeaturedOrganizer: roles.includes('featured_organizer')
    };
  };

  const refreshRoles = async () => {
    if (user) {
      const { isAdmin, isSuperAdmin, isOrganizer, isFeaturedOrganizer } = await checkAdminStatus(user.id);
      setIsAdmin(isAdmin);
      setIsSuperAdmin(isSuperAdmin);
      setIsOrganizer(isOrganizer);
      setIsFeaturedOrganizer(isFeaturedOrganizer);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          setTimeout(async () => {
            const { isAdmin, isSuperAdmin, isOrganizer, isFeaturedOrganizer } = await checkAdminStatus(currentSession.user.id);
            setIsAdmin(isAdmin);
            setIsSuperAdmin(isSuperAdmin);
            setIsOrganizer(isOrganizer);
            setIsFeaturedOrganizer(isFeaturedOrganizer);
          }, 0);
        } else {
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsOrganizer(false);
          setIsFeaturedOrganizer(false);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        setTimeout(async () => {
          const { isAdmin, isSuperAdmin, isOrganizer, isFeaturedOrganizer } = await checkAdminStatus(currentSession.user.id);
          setIsAdmin(isAdmin);
          setIsSuperAdmin(isSuperAdmin);
          setIsOrganizer(isOrganizer);
          setIsFeaturedOrganizer(isFeaturedOrganizer);
          setLoading(false);
        }, 0);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);


  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      throw error;
    }

    // Check if email is verified via our custom verification system
    if (data.user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('email_verified')
        .eq('id', data.user.id)
        .single();
      
      if (!profile?.email_verified) {
        toast.error('Please verify your email before signing in. Check your inbox for the verification link.');
        await supabase.auth.signOut();
        throw new Error('Email not verified');
      }
    }

    toast.success('Welcome back!');
    navigate('/');
  };

  const signUp = async (email: string, password: string): Promise<{ user: User | null }> => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (error) {
      toast.error(error.message);
      throw error;
    }

    // Note: We don't show a success toast here or navigate away
    // The Auth.tsx component will handle showing the verification message
    return { user: data.user };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      toast.error(error.message);
      throw error;
    }

    toast.success('Signed out successfully');
    navigate('/');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAdmin,
        isSuperAdmin,
        isOrganizer,
        isFeaturedOrganizer,
        loading,
        signIn,
        signUp,
        signOut,
        refreshRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
